import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { Link, type LinkProps } from 'react-router';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

const base =
  'relative inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium transition duration-150 ' +
  'disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 active:translate-y-px motion-reduce:transition-none';

const variants: Record<ButtonVariant, string> = {
  primary:
    'btn-sheen text-white shadow-sm',
  secondary:
    'border border-line-strong bg-surface text-ink shadow-card hover:border-sapphire-300 hover:bg-sunken dark:hover:border-sapphire-600',
  ghost: 'text-ink-muted hover:bg-sunken hover:text-ink',
  danger:
    'bg-danger-600 text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.18)] hover:bg-danger-700 hover:shadow-[0_8px_24px_-8px_rgb(220_38_38/0.55)]',
  subtle: 'bg-sapphire-50 text-sapphire-800 hover:bg-sapphire-100 dark:bg-sapphire-900/40 dark:text-sapphire-200 dark:hover:bg-sapphire-900/70',
};

const sizes: Record<ButtonSize, string> = {
  xs: 'h-7 px-2.5 text-xs',
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

export function buttonClasses({
  variant = 'primary',
  size = 'md',
  fullWidth,
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
} = {}): string {
  return cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className);
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  tone?: ButtonVariant | string;
  size?: ButtonSize;
  /** Shows a spinner, disables the button and sets aria-busy. */
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

export function Button({ variant, tone, size = 'md', loading = false, leftIcon, rightIcon, fullWidth, className, children, disabled, type = 'button', ref, ...rest }: ButtonProps) {
  const resolvedVariant = (variant ?? (tone as ButtonVariant) ?? 'primary');
  return (
    <button
      ref={ref}
      type={type}
      className={buttonClasses({ variant: resolvedVariant, size, fullWidth, className })}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={size === 'lg' ? 'md' : 'sm'} /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
}

export interface ButtonLinkProps extends LinkProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

/** A react-router <Link> that looks like a Button. */
export function ButtonLink({ variant = 'primary', size = 'md', leftIcon, rightIcon, fullWidth, className, children, ...rest }: ButtonLinkProps) {
  return (
    <Link className={buttonClasses({ variant, size, fullWidth, className })} {...rest}>
      {leftIcon}
      {children}
      {rightIcon}
    </Link>
  );
}

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /** Required: icon-only controls need an accessible name. */
  label: string;
  icon: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

const iconSizes: Record<ButtonSize, string> = { xs: 'h-7 w-7', sm: 'h-8 w-8', md: 'h-10 w-10', lg: 'h-12 w-12' };

export function IconButton({ label, icon, variant = 'ghost', size = 'md', loading, className, disabled, type = 'button', ref, ...rest }: IconButtonProps) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(base, variants[variant], iconSizes[size], 'px-0', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size="sm" /> : icon}
    </button>
  );
}
